// src/components/modals/AgentUploadModal.tsx
// Complete enhanced version preserving ALL your existing functionality
//import React, { useState, useCallback } from 'react';
import { agentManagerApi } from '../../config/api';
import React, { useState, useCallback, useEffect } from 'react';

// Environment-aware API configuration (YOUR EXISTING)
const getApiBaseUrl = () => {
    // First check if explicitly set (for Docker and GCP)
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }

    // Development fallback
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8000";
    }

    // Production: use hostname replacement only as last resort
    const hostname = window.location.hostname;

    // Check if we're on localhost (production build running locally)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return "http://localhost:8000";
    }

    // GCP production - replace frontend-alpha with orchestrator
    const apiHost = hostname.replace('frontend-alpha', 'orchestrator');
    return `https://${apiHost}`;
};

const API_BASE_URL = getApiBaseUrl();

interface AgentUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadComplete: () => void;
    setIsUploading: (uploading: boolean) => void;
}

const AgentUploadModal: React.FC<AgentUploadModalProps> = ({
    isOpen, onClose, onUploadComplete, setIsUploading
}) => {
    // ALL YOUR EXISTING STATE VARIABLES
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadStatus, setUploadStatus] = useState<string>('');
    const [conflictStrategy, setConflictStrategy] = useState<'merge' | 'replace'>('merge');
    const [isUploadingLocal, setIsUploadingLocal] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);

    // YOUR EXISTING DEPENDENCY STATES
    const [showDependencies, setShowDependencies] = useState(false);
    //const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>(['langchain-full']); // Default
    const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]); // No Default

    const [customRequirements, setCustomRequirements] = useState('');

    // NEW: Enhanced states for better UX
    const [suggestedFrameworks, setSuggestedFrameworks] = useState<string[]>([]);
    const [installationResults, setInstallationResults] = useState<any>(null);


    // NEW: Tab management state
    const [currentTab, setCurrentTab] = useState<'upload' | 'template' | 'context' | 'preview'>('upload');
    const [tabsCompleted, setTabsCompleted] = useState({
        upload: false,
        template: false,
        context: false,
        preview: false
    });

    // NEW: Template selection state
    const [selectedDomain, setSelectedDomain] = useState<string>('');
    const [customDocuments, setCustomDocuments] = useState<File[]>([]);
    const [userTextPrompt, setUserTextPrompt] = useState<string>('');
    const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

    // Add with other state variables
    const [applicationName, setApplicationName] = useState<string>('');
    const [existingApplications, setExistingApplications] = useState<string[]>([]);
    const [useExistingApp, setUseExistingApp] = useState<boolean>(true);
    const [selectedApplication, setSelectedApplication] = useState<string>('');

    // Function to fetch existing applications
    const fetchExistingApplications = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/db/applications`);
            if (response.ok) {
                const apps = await response.json();
                setExistingApplications(apps.map((app: any) => app.name));
                if (apps.length === 0) {
                    setUseExistingApp(false);
                }
            } else if (response.status === 404) {
                // No applications endpoint yet, default to create new
                console.log('Applications endpoint not available yet');
                setUseExistingApp(false);
            }
        } catch (error) {
            console.error('Failed to fetch applications:', error);
            setUseExistingApp(false);
        }
    };

    // NEW: Template helper function
    const getTemplatesForDomain = (domain: string) => {
        const templates = {
            telecommunications: [
                { id: 'sim_ordering', name: 'SIM Ordering', description: 'Customer SIM card ordering and activation' },
                { id: 'network_support', name: 'Network Support', description: 'Network troubleshooting and support' },
                { id: 'billing_inquiries', name: 'Billing Support', description: 'Billing and payment inquiries' }
            ],
            ecommerce: [
                { id: 'order_processing', name: 'Order Processing', description: 'Order placement and tracking' },
                { id: 'customer_service', name: 'Customer Service', description: 'General customer support' },
                { id: 'returns_refunds', name: 'Returns & Refunds', description: 'Return and refund processing' }
            ],
            finance: [
                { id: 'account_management', name: 'Account Management', description: 'Banking account operations' },
                { id: 'transaction_support', name: 'Transaction Support', description: 'Payment and transaction help' },
                { id: 'compliance_checks', name: 'Compliance', description: 'Regulatory compliance validation' }
            ],
            healthcare: [
                { id: 'appointment_scheduling', name: 'Appointments', description: 'Schedule and manage appointments' },
                { id: 'patient_records', name: 'Patient Records', description: 'Access and update patient information' },
                { id: 'insurance_billing', name: 'Insurance & Billing', description: 'Insurance and billing support' }
            ],
            general: [
                { id: 'general_support', name: 'General Support', description: 'Basic customer service template' }
            ]
        };

        return templates[domain] || templates.general;
    };

    const resetModalState = useCallback(() => {
        setSelectedFile(null);
        setUploadStatus('');
        setUploadComplete(false);
        setIsUploadingLocal(false);
        setShowDependencies(false);
        setSelectedFrameworks(['langchain-full']);
        setCustomRequirements('');
        setSuggestedFrameworks([]);
        setInstallationResults(null);
        setDragActive(false);
        setConflictStrategy('merge');

        setCurrentTab('upload');  // Reset to first tab
        setSelectedDomain('');    // Clear domain selection
        setSelectedTemplates([]); // Clear template selections
        setCustomDocuments([]);   // Clear uploaded documents
        setUserTextPrompt('');    // Clear user instructions
        setTabsCompleted({        // Reset tab completion state
            upload: false,
            template: false,
            context: false,
            preview: false
        });
    }, []);

    const handleClose = useCallback(() => {
        if (isUploadingLocal) return;
        resetModalState();
        onClose();
    }, [isUploadingLocal, resetModalState, onClose]);

    const handleDone = useCallback(() => {
        resetModalState();
        onClose();
    }, [resetModalState, onClose]);

    // ENHANCED: Extended framework options (8 frameworks)
    const quickFrameworks = [
        {
            id: 'langchain-full',
            name: 'LangChain Full',
            deps: 'langchain-anthropic langchain-google-genai langgraph',
            description: 'Complete LangChain ecosystem with all providers'
        },
        {
            id: 'pydantic-ai',
            name: 'PydanticAI',
            deps: 'pydantic-ai>=0.0.13',
            description: 'Type-safe AI agents with Pydantic'
        },
        {
            id: 'crewai',
            name: 'CrewAI',
            deps: 'crewai>=0.70.0',
            description: 'Multi-agent collaboration framework'
        },
        {
            id: 'autogen',
            name: 'AutoGen',
            deps: 'autogen-agentchat>=0.2.0',
            description: 'Microsoft AutoGen agents'
        },
        {
            id: 'data-processing',
            name: 'Data Processing',
            deps: 'scipy matplotlib scikit-learn',
            description: 'Scientific computing and visualization'
        },
        {
            id: 'web-scraping',
            name: 'Web Scraping',
            deps: 'beautifulsoup4 selenium',
            description: 'Web scraping and automation tools'
        },
        {
            id: 'vector-db',
            name: 'Vector Database',
            deps: 'chromadb faiss-cpu',
            description: 'Vector storage and similarity search'
        },
        {
            id: 'documents',
            name: 'Document Processing',
            deps: 'PyPDF2 python-docx openpyxl',
            description: 'PDF, Word, Excel processing'
        }
    ];

    // NEW: Domain options for template selection
    const domainOptions = [
        { id: 'telecommunications', name: 'Telecommunications', description: 'SIM ordering, network support, billing' },
        { id: 'ecommerce', name: 'E-commerce', description: 'Order processing, returns, customer service' },
        { id: 'finance', name: 'Finance & Banking', description: 'Account management, transactions, compliance' },
        { id: 'healthcare', name: 'Healthcare', description: 'Patient care, appointments, medical records' },
        { id: 'general', name: 'General Purpose', description: 'Custom business logic and workflows' }
    ];

    //  EXISTING DRAG & DROP HANDLERS
    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = Array.from(e.dataTransfer.files);
        const zipFile = files.find(file => file.name.endsWith('.zip'));

        if (zipFile) {
            setSelectedFile(zipFile);
        } else {
            alert('Please upload a ZIP file containing the agent package.');
        }
    }, []);


    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setIsUploadingLocal(true);
        setUploadStatus('Starting agent upload...');

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            // Add dependency information
            const frameworkDeps = selectedFrameworks
                .map(id => quickFrameworks.find(f => f.id === id)?.deps)
                .filter(Boolean)
                .join(' ');
            const customDeps = customRequirements.trim().split('\n').filter(Boolean).join(' ');
            const allDeps = [frameworkDeps, customDeps].filter(Boolean).join(' ');

            if (allDeps) {
                formData.append('additional_dependencies', allDeps);
                formData.append('selected_frameworks', JSON.stringify(selectedFrameworks));
            }

            // Add application name
            const appName = useExistingApp ? selectedApplication : applicationName;
            if (appName) {
                formData.append('application_name', appName);
            }

            setUploadStatus('Submitting to background processing...');
            const response = await fetch(`${API_BASE_URL}/agent-manager/upload-intelligent`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Server Error: ${response.status} - ${errorText}`);
            }

            const result = await response.json();

            // Check if it's a background job (202 response)
            if (result.status === 'accepted' && result.job_id) {
                setUploadStatus('Upload accepted! Processing in background...');
                setUploadComplete(true);
                //onUploadComplete();

                // Start polling for job status
                startJobPolling(result.job_id);
                return;
            }

            // Then in startJobPolling, when status is COMPLETED:
            if (jobStatus.status === 'COMPLETED') {
                clearInterval(pollInterval);
                const agentName = jobStatus.result?.metadata?.name || jobStatus.agent_name;
                setUploadStatus(`Agent "${agentName}" is ready!`);

                // NOW call onUploadComplete when agent is actually ready
                onUploadComplete();  // CORRECT - Agent is ready now!
            }

            // Handle direct success (fallback for legacy)
            if (result.success) {
                const agentName = result.metadata?.name || "Unknown Agent";
                setUploadStatus(`Agent "${agentName}" uploaded successfully!`);
                setUploadComplete(true);
                onUploadComplete();
                return;
            }

            // If we get here, something unexpected happened
            throw new Error(`Unexpected response: ${JSON.stringify(result)}`);

        } catch (error: any) {
            setUploadStatus(`❌ Upload failed: ${error.message}`);
            console.error('Agent upload error:', error);
        } finally {
            setIsUploading(false);
            setIsUploadingLocal(false);
        }
    };

    const handleBasicUpload = async () => {
        if (!selectedFile) return;

        setIsUploadingLocal(true);
        setIsUploading(true);
        setUploadStatus('Starting basic agent upload...');

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            // Add framework selections
            if (selectedFrameworks.length > 0) {
                const frameworkDeps = selectedFrameworks
                    .map(id => quickFrameworks.find(f => f.id === id)?.deps)
                    .filter(Boolean)
                    .join(' ');
                formData.append('additional_dependencies', frameworkDeps);
                formData.append('selected_frameworks', JSON.stringify(selectedFrameworks));
            }

            formData.append('conflict_strategy', conflictStrategy);

            // Add application name
            const appName = useExistingApp ? selectedApplication : applicationName;
            if (appName) {
                formData.append('application_name', appName);
            }

            const uploadResponse = await fetch(`${API_BASE_URL}/agent-manager/upload-intelligent`, {
                method: 'POST',
                body: formData
            });

            if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
            }

            const result = await uploadResponse.json();

            if (result.success) {
                setUploadStatus('Agent upload completed successfully!');
                setUploadComplete(true);
                setTabsCompleted({
                    upload: true,
                    template: true,
                    context: true,
                    preview: true
                });
                onUploadComplete();
            } else {
                throw new Error(result.error || 'Upload failed');
            }

        } catch (error) {
            console.error('Upload error:', error);
            setUploadStatus(`❌ Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsUploadingLocal(false);
            setIsUploading(false);
        }
    };

    const handleEnhancedUpload = async () => {
        if (!selectedFile || !selectedDomain) {
            setUploadStatus('❌ Please complete agent upload and domain selection first.');
            return;
        }

        setIsUploadingLocal(true);
        setIsUploading(true);
        setUploadStatus('Starting enhanced upload...');

        try {
            // Step 1: Upload custom documents if any
            let documentIds: string[] = [];
            if (customDocuments.length > 0) {
                setUploadStatus('Uploading custom documents...');

                for (const doc of customDocuments) {
                    const formData = new FormData();
                    formData.append('file', doc);

                    const docResponse = await fetch(`${API_BASE_URL}/document-intelligence/upload-document`, {
                        method: 'POST',
                        body: formData
                    });

                    if (docResponse.ok) {
                        const docResult = await docResponse.json();
                        documentIds.push(docResult.document_id);
                        setUploadStatus(prev => prev + `\n✅ Uploaded: ${doc.name}`);
                    } else {
                        console.warn(`Failed to upload document: ${doc.name}`);
                        setUploadStatus(prev => prev + `\n⚠️ Failed to upload: ${doc.name}`);
                    }
                }
            }

            // Step 2: Prepare enhanced context data
            const enhancedContext = {
                selected_domain: selectedDomain,
                selected_templates: selectedTemplates,
                custom_documents: documentIds,
                user_text_prompt: userTextPrompt.trim() || null,
                application_name: useExistingApp ? selectedApplication : applicationName,
                template_fields: [],
                hybrid_mode: true
            };

            // Step 3: Upload agent with enhanced context
            setUploadStatus(prev => prev + '\nUploading agent with enhanced context...');

            const formData = new FormData();
            formData.append('file', selectedFile);

            // Add framework selections
            if (selectedFrameworks.length > 0) {
                formData.append('selected_frameworks', selectedFrameworks.join(','));
            }

            if (customRequirements.trim()) {
                formData.append('additional_dependencies', customRequirements.trim());
            }

            // Add enhanced context as JSON
            formData.append('enhanced_context', JSON.stringify(enhancedContext));
            formData.append('conflict_strategy', conflictStrategy);

            const uploadResponse = await fetch(`${API_BASE_URL}/agent-manager/upload-intelligent-enhanced`, {
                method: 'POST',
                body: formData
            });

            if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
            }

            const result = await uploadResponse.json();

            if (result.success) {
                setUploadStatus(prev => prev + '\n✅ Enhanced agent upload completed successfully!');
                setInstallationResults(result.installation_results);
                setSuggestedFrameworks(result.suggested_frameworks || []);
                setUploadComplete(true);

                // Update tab completion
                setTabsCompleted({
                    upload: true,
                    template: true,
                    context: true,
                    preview: true
                });

                // Show success message with enhanced features
                setUploadStatus(prev => prev + `
                Agent uploaded with enhanced context
                Domain: ${domainOptions.find(d => d.id === selectedDomain)?.name}
                Documents: ${customDocuments.length} uploaded
                Custom instructions: ${userTextPrompt.trim() ? 'Provided' : 'None'}
                Ready for intelligent test generation!`);

                // Trigger refresh of agent list
                onUploadComplete();
                setTimeout(() => {
                    handleClose();  // This will reset and close the modal
                }, 2000);  // 2 second delay to show success message

            } else {
                throw new Error(result.error || 'Enhanced upload failed');
            }

        } catch (error) {
            console.error('Enhanced upload error:', error);
            setUploadStatus(`❌ Enhanced upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsUploadingLocal(false);
            setIsUploading(false);
        }
    };

    // Also add this helper function to handle regular upload completion
    const handleUploadComplete = () => {
        if (uploadComplete) {
            // If we're in enhanced mode, continue to templates
            setTabsCompleted({ ...tabsCompleted, upload: true });
            setCurrentTab('template');
        }
    };


    const startJobPolling = (jobId: string) => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/installation-status/${jobId}`);

                if (!response.ok) {
                    console.error('Failed to check job status:', response.status);
                    clearInterval(pollInterval);
                    return;
                }

                const jobStatus = await response.json();

                // Update UI based on status
                if (jobStatus.status === 'PENDING') {
                    setUploadStatus(`${jobStatus.message} (${jobStatus.progress}%)`);
                } else if (jobStatus.status === 'PROCESSING') {
                    setUploadStatus(`${jobStatus.message} (${jobStatus.progress}%)`);
                } else if (jobStatus.status === 'COMPLETED') {
                    // Success!
                    clearInterval(pollInterval);
                    const agentName = jobStatus.result?.metadata?.name || jobStatus.agent_name;
                    setUploadStatus(`Agent "${agentName}" is ready!`);

                    // Show installation results if available
                    if (jobStatus.result?.dependency_installation) {
                        setInstallationResults(jobStatus.result.dependency_installation);
                    }

                    // Show AI suggestions if available
                    if (jobStatus.result?.suggested_frameworks) {
                        setSuggestedFrameworks(jobStatus.result.suggested_frameworks);
                    }

                    // Show success toast notification
                    showToast(`Agent "${agentName}" is now ready!`, 'success');

                } else if (jobStatus.status === 'FAILED') {
                    // Failed!
                    clearInterval(pollInterval);
                    setUploadStatus(`❌ Upload failed: ${jobStatus.error}`);

                    // Show error toast notification
                    showToast(`❌ Agent upload failed: ${jobStatus.error}`, 'error');
                }

            } catch (error) {
                console.error('Error polling job status:', error);
                clearInterval(pollInterval);
                setUploadStatus('❌ Lost connection to server');
            }
        }, 5000); // Poll every 5 seconds

        // Cleanup after 10 minutes (timeout)
        setTimeout(() => {
            clearInterval(pollInterval);
        }, 600000);
    };


    const showToast = (message: string, type: 'success' | 'error') => {
        // Create toast element
        const toast = document.createElement('div');
        toast.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        padding: 1rem 1.5rem;
        border-radius: 8px;
        color: white;
        font-weight: bold;
        z-index: 10000;
        max-width: 400px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        background-color: ${type === 'success' ? '#28a745' : '#dc3545'};
        transform: translateX(100%);
        transition: transform 0.3s ease;
    `;
        toast.textContent = message;

        // Add to page
        document.body.appendChild(toast);

        // Animate in
        setTimeout(() => {
            toast.style.transform = 'translateX(0)';
        }, 100);

        // Auto-remove after 5 seconds
        setTimeout(() => {
            toast.style.transform = 'translateX(100%)';
            setTimeout(() => {
                if (toast.parentNode) {
                    document.body.removeChild(toast);
                }
            }, 300);
        }, 5000);
    };


    // Fetch applications when modal opens
    // Add useEffect import at the top if not there

    useEffect(() => {
        if (isOpen) {
            fetchExistingApplications();
        }
    }, [isOpen]); // Only fetch when modal opens/closes

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={isUploadingLocal ? undefined : handleClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <h2>Upload Agent Package</h2>

                {/* Tab Navigation */}
                <div className="tab-navigation" style={{
                    display: 'flex',
                    borderBottom: '2px solid #e0e0e0',
                    marginBottom: '1.5rem'
                }}>
                    {[
                        { id: 'upload', label: 'Agent Upload' },
                        { id: 'template', label: 'Domain & Context' },
                        { id: 'context', label: 'Custom Documents' },
                        { id: 'preview', label: 'Preview & Upload' }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setCurrentTab(tab.id as any)}
                            disabled={tab.id !== 'upload' && !selectedFile}

                            style={{
                                flex: 1,
                                padding: '0.75rem',
                                border: 'none',
                                background: currentTab === tab.id ? '#007bff' : 'transparent',
                                color: currentTab === tab.id ? 'white' : '#666',
                                cursor: tab.id === 'upload' || tabsCompleted.upload ? 'pointer' : 'not-allowed',
                                opacity: tab.id === 'upload' || tabsCompleted.upload ? 1 : 0.5,
                                fontSize: '0.9rem',
                                fontWeight: currentTab === tab.id ? 'bold' : 'normal',
                                transition: 'all 0.2s'
                            }}
                        >
                            {tab.icon} {tab.label}
                            {tabsCompleted[tab.id as keyof typeof tabsCompleted] && tab.id !== currentTab && ' ✓'}
                        </button>
                    ))}
                </div>

                {/* Tab Content Container */}
                <div className="tab-content" style={{ minHeight: '400px' }}>

                    {/* Tab 1: Agent Upload */}
                    {currentTab === 'upload' && (
                        <div className="upload-tab">
                            <div
                                className={`upload-zone ${dragActive ? 'active' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                style={{
                                    border: '2px dashed #ccc',
                                    borderRadius: '8px',
                                    padding: '2rem',
                                    textAlign: 'center',
                                    backgroundColor: dragActive ? '#f0f8ff' : '#fafafa'
                                }}
                            >
                                {selectedFile ? (
                                    <div>
                                        <p>{selectedFile.name}</p>
                                        <p style={{ fontSize: '0.9rem', color: '#666' }}>
                                            Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        <p>Drop your agent ZIP package here</p>
                                        <p style={{ fontSize: '0.9rem', color: '#666' }}>
                                            Or click to browse files
                                        </p>
                                    </div>
                                )}

                                <input
                                    type="file"
                                    accept=".zip"
                                    onChange={(e) => {
                                        //console.log('File input change triggered:', e.target.files);
                                        if (e.target.files && e.target.files[0]) {
                                            const file = e.target.files[0];
                                            //console.log('Selected file:', file.name, file.size);
                                            setSelectedFile(file);
                                            // Reset the input value to allow re-selection of same file
                                            e.target.value = '';
                                        } else {
                                            //console.log('No file selected or files array empty');
                                        }
                                    }}
                                    style={{ display: 'none' }}
                                    id="file-upload"
                                />
                                <button
                                    className="pushable push-secondary btn-sm"
                                    type="button"
                                    onClick={(e) => {
                                        //console.log('Browse Files button clicked');
                                        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                                        if (fileInput) {
                                            fileInput.click();
                                        } else {
                                            console.error('File input element not found');
                                        }
                                    }}
                                >
                                    <span className="shadow"></span>
                                    <span className="edge"></span>
                                    <span className="front">Browse Files</span>
                                </button>
                            </div>

                            {selectedFile && (
                                <div style={{ marginTop: '1rem' }}>
                                    <button
                                        onClick={() => setShowDependencies(!showDependencies)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#007bff',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}
                                    >
                                        {showDependencies ? '🔽' : '▶️'} Configure Dependencies ({selectedFrameworks.length} selected)
                                    </button>

                                    {showDependencies && (
                                        <div style={{
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            padding: '1rem',
                                            marginTop: '0.5rem',
                                            backgroundColor: '#f8f9fa'
                                        }}>
                                            <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Available Frameworks:</h4>

                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                                gap: '0.75rem',
                                                marginBottom: '1rem'
                                            }}>
                                                {quickFrameworks.map(framework => (
                                                    <label key={framework.id} style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '0.25rem',
                                                        cursor: 'pointer',
                                                        padding: '0.75rem',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '6px',
                                                        backgroundColor: selectedFrameworks.includes(framework.id) ? '#e3f2fd' : '#fff',
                                                        transition: 'all 0.2s ease'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedFrameworks.includes(framework.id)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedFrameworks([...selectedFrameworks, framework.id]);
                                                                    } else {
                                                                        setSelectedFrameworks(selectedFrameworks.filter(id => id !== framework.id));
                                                                    }
                                                                }}
                                                            />
                                                            <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                {framework.name}
                                                            </span>
                                                        </div>
                                                        <span style={{ fontSize: '0.8rem', color: '#666', lineHeight: '1.3' }}>
                                                            {framework.description}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>

                                            <div>
                                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                                    Custom Requirements (one per line):
                                                </label>
                                                <textarea
                                                    value={customRequirements}
                                                    onChange={(e) => setCustomRequirements(e.target.value)}
                                                    placeholder={`transformers>=4.45.0\ntorch>=2.4.0\npillow>=10.4.0\n# Add custom dependencies here`}
                                                    rows={4}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.5rem',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.9rem'
                                                    }}
                                                />
                                            </div>

                                            {(selectedFrameworks.length > 0 || customRequirements.trim()) && (
                                                <div style={{
                                                    marginTop: '1rem',
                                                    padding: '0.75rem',
                                                    backgroundColor: '#fff3cd',
                                                    border: '1px solid #ffeaa7',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    <strong>Will install ({selectedFrameworks.length + (customRequirements.trim().split('\n').filter(line => line.trim() && !line.startsWith('#')).length)} packages):</strong>
                                                    <br />
                                                    <code style={{ wordBreak: 'break-all', display: 'block', marginTop: '0.5rem' }}>
                                                        {selectedFrameworks
                                                            .map(id => quickFrameworks.find(f => f.id === id)?.deps)
                                                            .filter(Boolean)
                                                            .join(' ')} {customRequirements.trim()}
                                                    </code>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {suggestedFrameworks.length > 0 && (
                                <div style={{
                                    marginTop: '1rem',
                                    padding: '1rem',
                                    backgroundColor: '#e8f5e8',
                                    border: '1px solid #4caf50',
                                    borderRadius: '6px'
                                }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32' }}>🤖 AI-Detected Frameworks:</h4>
                                    <div style={{ fontSize: '0.9rem', color: '#2e7d32' }}>
                                        Your agent appears to use: <strong>{suggestedFrameworks.join(', ')}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                                        Consider selecting these frameworks for optimal performance.
                                    </div>
                                </div>
                            )}

                            {installationResults && (
                                <div style={{
                                    marginTop: '1rem',
                                    padding: '1rem',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px'
                                }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0' }}>📦 Installation Results:</h4>

                                    {installationResults.installed?.length > 0 && (
                                        <div style={{ marginBottom: '0.5rem' }}>
                                            <strong style={{ color: '#28a745' }}>Successfully installed ({installationResults.installed.length}):</strong>
                                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                                {installationResults.installed.map((pkg: any) => pkg.package || pkg).join(', ')}
                                            </div>
                                        </div>
                                    )}

                                    {installationResults.failed?.length > 0 && (
                                        <div>
                                            <strong style={{ color: '#dc3545' }}>❌ Failed to install ({installationResults.failed.length}):</strong>
                                            <ul style={{ margin: '0.25rem 0 0 1.5rem', fontSize: '0.85rem', color: '#666' }}>
                                                {installationResults.failed.map((fail: any, index: number) => (
                                                    <li key={index}>{fail.package}: {fail.error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ marginTop: '1rem' }}>
                                <label style={{ fontWeight: 'bold' }}>If agent already exists:</label>
                                <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="radio"
                                            value="merge"
                                            checked={conflictStrategy === 'merge'}
                                            onChange={e => setConflictStrategy(e.target.value as 'merge')}
                                        />
                                        Merge with existing
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="radio"
                                            value="replace"
                                            checked={conflictStrategy === 'replace'}
                                            onChange={e => setConflictStrategy(e.target.value as 'replace')}
                                        />
                                        Replace completely
                                    </label>
                                </div>
                            </div>

                            {uploadStatus && (
                                <div style={{
                                    marginTop: '1rem',
                                    padding: '0.75rem',
                                    backgroundColor: uploadStatus.includes('❌') ? '#f8d7da' : '#d4edda',
                                    borderRadius: '6px',
                                    fontSize: '0.9rem',
                                    whiteSpace: 'pre-line'
                                }}>
                                    {uploadStatus}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab 2: Domain & Template Selection */}
                    {currentTab === 'template' && (
                        <div className="template-tab">
                            <h3>Domain & Template Selection</h3>

                            {/* Application Selection/Creation */}
                            <div className="control-group" style={{ marginBottom: '1.5rem' }}>
                                <label>Application *</label>

                                {/* Radio buttons to choose between existing or new */}
                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                                    <label>
                                        <input
                                            type="radio"
                                            checked={useExistingApp}
                                            onChange={() => setUseExistingApp(true)}
                                            disabled={existingApplications.length === 0}
                                        />
                                        Select Existing
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            checked={!useExistingApp}
                                            onChange={() => setUseExistingApp(false)}
                                        />
                                        Create New
                                    </label>
                                </div>

                                {/* Show dropdown or text input based on selection */}
                                {useExistingApp ? (
                                    <select
                                        value={selectedApplication}
                                        onChange={(e) => setSelectedApplication(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            fontSize: '1rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <option value="">-- Select Application --</option>
                                        {existingApplications.map(app => (
                                            <option key={app} value={app}>{app}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={applicationName}
                                        onChange={(e) => setApplicationName(e.target.value)}
                                        placeholder="e.g., BillingSystem, UniverseCare, myApp"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            fontSize: '1rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px'
                                        }}
                                    />
                                )}

                                <small style={{ color: '#666' }}>
                                    Group related agents and APIs under this application
                                </small>
                            </div>

                            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                                Select your business domain to get intelligent test generation tailored to your industry.
                            </p>

                            {/* Domain Selection */}
                            <div style={{ marginBottom: '2rem' }}>
                                <h4 style={{ marginBottom: '1rem' }}>Business Domain</h4>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                    gap: '1rem'
                                }}>
                                    {domainOptions.map(domain => (
                                        <label key={domain.id} style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem',
                                            cursor: 'pointer',
                                            padding: '1rem',
                                            border: '2px solid #ddd',
                                            borderRadius: '8px',
                                            backgroundColor: selectedDomain === domain.id ? '#e3f2fd' : '#fff',
                                            borderColor: selectedDomain === domain.id ? '#007bff' : '#ddd',
                                            transition: 'all 0.2s ease',
                                            position: 'relative'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <input
                                                    type="radio"
                                                    name="domain"
                                                    value={domain.id}
                                                    checked={selectedDomain === domain.id}
                                                    onChange={(e) => setSelectedDomain(e.target.value)}
                                                    style={{
                                                        transform: 'scale(1.2)',
                                                        accentColor: '#007bff'
                                                    }}
                                                />
                                                <span style={{
                                                    fontSize: '1rem',
                                                    fontWeight: 'bold',
                                                    color: selectedDomain === domain.id ? '#007bff' : '#333'
                                                }}>
                                                    {domain.name}
                                                </span>
                                            </div>
                                            <span style={{
                                                fontSize: '0.9rem',
                                                color: '#666',
                                                lineHeight: '1.4',
                                                marginLeft: '2rem'
                                            }}>
                                                {domain.description}
                                            </span>
                                            {selectedDomain === domain.id && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '0.5rem',
                                                    right: '0.5rem',
                                                    color: '#007bff',
                                                    fontSize: '1.2rem'
                                                }}>
                                                    ✓
                                                </div>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Template Selection */}
                            {selectedDomain && (
                                <div style={{ marginBottom: '2rem' }}>
                                    <h4 style={{ marginBottom: '1rem' }}>Available Templates</h4>
                                    <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                        Choose specific templates for {domainOptions.find(d => d.id === selectedDomain)?.name} domain.
                                    </p>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: '1rem'
                                    }}>
                                        {getTemplatesForDomain(selectedDomain).map(template => (
                                            <label key={template.id} style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '0.5rem',
                                                cursor: 'pointer',
                                                padding: '1rem',
                                                border: '2px solid #ddd',
                                                borderRadius: '8px',
                                                backgroundColor: selectedTemplates.includes(template.id) ? '#e3f2fd' : '#fff',
                                                borderColor: selectedTemplates.includes(template.id) ? '#007bff' : '#ddd',
                                                transition: 'all 0.2s ease'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTemplates.includes(template.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedTemplates([...selectedTemplates, template.id]);
                                                            } else {
                                                                setSelectedTemplates(selectedTemplates.filter(id => id !== template.id));
                                                            }
                                                        }}
                                                        style={{
                                                            transform: 'scale(1.2)',
                                                            accentColor: '#007bff'
                                                        }}
                                                    />
                                                    <span style={{ fontWeight: 'bold', color: selectedTemplates.includes(template.id) ? '#007bff' : '#333' }}>
                                                        {template.name}
                                                    </span>
                                                </div>
                                                <span style={{
                                                    fontSize: '0.9rem',
                                                    color: '#666',
                                                    lineHeight: '1.4',
                                                    marginLeft: '1.7rem'
                                                }}>
                                                    {template.description}
                                                </span>
                                            </label>
                                        ))}
                                    </div>

                                    {selectedTemplates.length > 0 && (
                                        <div style={{
                                            marginTop: '1rem',
                                            padding: '0.75rem',
                                            backgroundColor: '#e8f5e8',
                                            border: '1px solid #4caf50',
                                            borderRadius: '4px',
                                            fontSize: '0.9rem'
                                        }}>
                                            <strong>Selected:</strong> {selectedTemplates.length} template(s) - {selectedTemplates.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Domain-Specific Info Display */}
                            {selectedDomain && selectedDomain !== 'general' && (
                                <div style={{
                                    padding: '1rem',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    marginBottom: '2rem'
                                }}>
                                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#007bff' }}>
                                        📋 {domainOptions.find(d => d.id === selectedDomain)?.name} Test Focus Areas
                                    </h4>
                                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                        {selectedDomain === 'telecommunications' && (
                                            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                                                <li>SIM ordering and activation workflows</li>
                                                <li>Network troubleshooting and support</li>
                                                <li>Billing inquiries and plan changes</li>
                                                <li>Service interruption handling</li>
                                            </ul>
                                        )}
                                        {selectedDomain === 'ecommerce' && (
                                            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                                                <li>Order processing and tracking</li>
                                                <li>Return and refund workflows</li>
                                                <li>Inventory and product inquiries</li>
                                                <li>Payment and shipping issues</li>
                                            </ul>
                                        )}
                                        {selectedDomain === 'finance' && (
                                            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                                                <li>Account management and transactions</li>
                                                <li>Compliance and regulatory requirements</li>
                                                <li>Fraud detection and security</li>
                                                <li>Investment and loan processing</li>
                                            </ul>
                                        )}
                                        {selectedDomain === 'healthcare' && (
                                            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                                                <li>Patient appointment scheduling</li>
                                                <li>Medical record access and privacy</li>
                                                <li>Insurance and billing support</li>
                                                <li>Prescription and treatment inquiries</li>
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* User Text Prompt */}
                            <div style={{ marginBottom: '2rem' }}>
                                <h4 style={{ marginBottom: '0.75rem' }}>Custom Instructions (Optional)</h4>
                                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                    Add specific requirements, edge cases, or focus areas for test generation.
                                </p>
                                <textarea
                                    value={userTextPrompt}
                                    onChange={(e) => setUserTextPrompt(e.target.value)}
                                    placeholder={`Examples:
    • Focus on edge cases with invalid customer IDs
    • Test multilingual support (English, Spanish, French)
    • Include scenarios with high-priority customers
    • Test integration with payment systems
    • Focus on GDPR compliance scenarios`}
                                    rows={6}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '0.9rem',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Tab 3: Custom Documents */}
                    {currentTab === 'context' && (
                        <div className="context-tab">
                            <h3>Custom Documents</h3>
                            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                                Upload business requirements, API documentation, or other relevant documents to enhance test generation.
                            </p>

                            {/* Document Upload Area */}
                            <div
                                className={`document-upload-zone ${dragActive ? 'active' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDragActive(false);

                                    const files = Array.from(e.dataTransfer.files).filter(file =>
                                        file.type === 'application/pdf' ||
                                        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                                        file.type === 'text/plain' ||
                                        file.name.endsWith('.md')
                                    );

                                    setCustomDocuments([...customDocuments, ...files]);
                                }}
                                style={{
                                    border: '2px dashed #ccc',
                                    borderRadius: '8px',
                                    padding: '2rem',
                                    textAlign: 'center',
                                    backgroundColor: dragActive ? '#f0f8ff' : '#fafafa',
                                    cursor: 'pointer',
                                    marginBottom: '1rem'
                                }}
                            >
                                <div>
                                    <p>📎 Drop business documents here</p>
                                    <p style={{ fontSize: '0.9rem', color: '#666' }}>
                                        Supported: PDF, DOCX, TXT, MD files
                                    </p>
                                </div>

                                <input
                                    type="file"
                                    accept=".pdf,.docx,.txt,.md"
                                    multiple
                                    onChange={(e) => {
                                        if (e.target.files) {
                                            const files = Array.from(e.target.files);
                                            setCustomDocuments([...customDocuments, ...files]);
                                        }
                                    }}
                                    style={{ display: 'none' }}
                                    id="document-upload"
                                />
                                <label htmlFor="document-upload" style={{ cursor: 'pointer' }}>
                                    <button className="pushable push-secondary btn-sm" type="button">
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">Browse Documents</span>
                                    </button>
                                </label>
                            </div>

                            {/* Uploaded Documents List */}
                            {customDocuments.length > 0 && (
                                <div style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    padding: '1rem',
                                    backgroundColor: '#f8f9fa'
                                }}>
                                    <h4 style={{ margin: '0 0 1rem 0' }}>Uploaded Documents ({customDocuments.length})</h4>
                                    {customDocuments.map((file, index) => (
                                        <div key={index} style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '0.5rem',
                                            backgroundColor: '#fff',
                                            border: '1px solid #eee',
                                            borderRadius: '4px',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <div>
                                                <span style={{ fontWeight: 'bold' }}>{file.name}</span>
                                                <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                                                    ({(file.size / 1024).toFixed(1)} KB)
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setCustomDocuments(customDocuments.filter((_, i) => i !== index));
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#dc3545',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2rem'
                                                }}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Document Types Info */}
                            <div style={{
                                marginTop: '1.5rem',
                                padding: '1rem',
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #90caf9',
                                borderRadius: '6px'
                            }}>
                                <h4 style={{ margin: '0 0 0.75rem 0', color: '#1976d2' }}>Document Types That Help</h4>
                                <div style={{ fontSize: '0.9rem', color: '#1976d2' }}>
                                    <strong>Business Requirements:</strong> User stories, acceptance criteria, business rules<br />
                                    <strong>API Documentation:</strong> OpenAPI specs, endpoint documentation<br />
                                    <strong>Compliance Docs:</strong> GDPR requirements, industry regulations<br />
                                    <strong>Process Flows:</strong> Workflow descriptions, decision trees
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab 4: Preview & Upload */}
                    {currentTab === 'preview' && (
                        <div className="preview-tab">
                            <h3>Context Preview</h3>
                            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                                Review your configuration before final upload. This context will be used to generate intelligent test cases.
                            </p>

                            {/* Context Summary */}
                            <div style={{
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <h4 style={{ margin: '0 0 1rem 0' }}>Configuration Summary</h4>

                                {/* Agent Info */}
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Agent:</strong> {selectedFile?.name || 'No agent selected'}
                                    {selectedFrameworks.length > 0 && (
                                        <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                                            Dependencies: {selectedFrameworks.join(', ')}
                                        </div>
                                    )}
                                </div>

                                {/* Domain Info */}
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Domain:</strong> {selectedDomain ?
                                        domainOptions.find(d => d.id === selectedDomain)?.name || 'Not selected' :
                                        'Not selected'
                                    }
                                </div>

                                {/* Documents Info */}
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Custom Documents:</strong> {customDocuments.length === 0 ?
                                        'None uploaded' :
                                        `${customDocuments.length} documents uploaded`
                                    }
                                    {customDocuments.length > 0 && (
                                        <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                                            {customDocuments.map(doc => doc.name).join(', ')}
                                        </div>
                                    )}
                                </div>

                                {/* User Prompt Info */}
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Custom Instructions:</strong> {userTextPrompt.trim() ?
                                        'Provided' :
                                        'None provided'
                                    }
                                    {userTextPrompt.trim() && (
                                        <div style={{
                                            fontSize: '0.9rem',
                                            color: '#666',
                                            marginTop: '0.5rem',
                                            padding: '0.5rem',
                                            backgroundColor: '#fff',
                                            border: '1px solid #eee',
                                            borderRadius: '4px',
                                            maxHeight: '100px',
                                            overflow: 'auto'
                                        }}>
                                            {userTextPrompt}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Next Steps Info */}
                            <div style={{
                                marginTop: '1.5rem',
                                padding: '1rem',
                                backgroundColor: '#e8f5e8',
                                border: '1px solid #4caf50',
                                borderRadius: '6px'
                            }}>
                                <h4 style={{ margin: '0 0 0.75rem 0', color: '#2e7d32' }}>🚀 What Happens Next</h4>
                                <div style={{ fontSize: '0.9rem', color: '#2e7d32' }}>
                                    ✅ Your agent will be uploaded with enhanced context<br />
                                    ✅ Domain-specific test templates will be applied<br />
                                    ✅ Custom documents will be analyzed for requirements<br />
                                    ✅ Intelligent test cases will be generated automatically
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Tab-aware Actions */}
                <div className="modal-actions">
                    <button className="pushable push-secondary" onClick={handleClose} disabled={isUploadingLocal}>
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">{isUploadingLocal ? 'Uploading...' : 'Cancel'}</span>
                    </button>

                    {/* Dynamic action buttons based on current tab */}
                    {currentTab === 'upload' && selectedFile && !uploadComplete ? (
                        <button
                            className="widget-button button-primary"
                            onClick={() => {
                                setTabsCompleted({ ...tabsCompleted, upload: true });
                                setCurrentTab('template');
                            }}
                            disabled={!selectedFile}
                        >
                            Continue to Templates
                        </button>
                    ) : currentTab === 'upload' && uploadComplete ? (
                        <button
                            className="widget-button button-primary"
                            onClick={() => {
                                setTabsCompleted({ ...tabsCompleted, upload: true });
                                setCurrentTab('template');
                            }}
                        >
                            Continue to Templates
                        </button>
                    ) : currentTab === 'template' ? (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="pushable push-secondary"
                                onClick={() => setCurrentTab('upload')}
                            >
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">⬅ Back</span>
                            </button>
                            <button
                                className="widget-button button-primary"
                                onClick={() => {
                                    setTabsCompleted({ ...tabsCompleted, template: true });
                                    setCurrentTab('context');
                                }}
                                disabled={!selectedDomain}
                            >
                                Continue to Documents
                            </button>
                        </div>
                    ) : currentTab === 'context' ? (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="pushable push-secondary"
                                onClick={() => setCurrentTab('template')}
                            >
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">⬅ Back</span>
                            </button>
                            <button
                                className="widget-button button-primary"
                                onClick={() => {
                                    setTabsCompleted({ ...tabsCompleted, context: true });
                                    setCurrentTab('preview');
                                }}
                            >
                                Continue to Preview
                            </button>
                        </div>
                    ) : currentTab === 'preview' ? (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="pushable push-secondary"
                                onClick={() => setCurrentTab('context')}
                            >
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">⬅ Back</span>
                            </button>
                            <button
                                className="widget-button button-primary"
                                onClick={() => {
                                    if (selectedDomain) {
                                        handleEnhancedUpload();
                                    } else {
                                        handleUpload();
                                    }
                                }}
                                disabled={!selectedFile || isUploadingLocal}
                            >
                                {isUploadingLocal
                                    ? 'Processing Upload...'
                                    : selectedDomain
                                        ? 'Complete Enhanced Upload'
                                        : 'Upload Agent'
                                }
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default AgentUploadModal;